import React, { useState, useCallback, useRef, useEffect } from 'react';
import DealerBrandingComponent from './DealerBrandingComponent';
import { getDealerLogo } from 'utils/theme';
import Description from '@mui/icons-material/ViewList';
import { Card, IconButton } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { BRANDING_QUERY, DEALERS_QUERY } from '../queries';
import { UPLOAD_APPLICATION_LOGO, SAVE_APPLICATION_THEME } from '../mutations';
import { APPLICATION_LOGO_UPLOADED, APPLICATION_THEME_SAVED } from '../subscriptions';
import { USER_INFO_QUERY } from 'components/queries';
import { useValidation } from '@totalsoft/pure-validations-react';
import { shape, required } from '@totalsoft/pure-validations';
import { useOidcUser, useOidcAccessToken } from '@axa-fr/react-oidc';
import { getOidcConfigName } from 'utils/functions';
import { emptyArray } from 'utils/constants';
import { useToast } from 'hooks/toasts/useToast';

export const brandingValidator = shape({ dealerId: required });

const defaultIsDirty = {
  logo: false,
  smallLogo: false,
  theme: false
};

const defaultIsSaving = {
  logo: false,
  smallLogo: false,
  theme: false
};

const defaultDealer = {
  id: null,
  name: ''
};

const DealerBrandingContainer = () => {
  const [isDirty, setIsDirty] = useState(defaultIsDirty);
  const [isSaving, setIsSaving] = useState(defaultIsSaving);
  const [localState, setLocalState] = useState({});
  const [logoFile, setLogoFile] = useState({ name: '' });
  const [smallLogoFile, setSmallLogoFile] = useState({ name: '' });
  const [dealer, setDealer] = useState(defaultDealer);
  const [validation, validate] = useValidation(brandingValidator);

  const logoFileRef = useRef();
  const smallLogoFileRef = useRef();
  const [t] = useTranslation();
  const addToast = useToast();
  const { oidcUser } = useOidcUser(getOidcConfigName());
  const { accessToken } = useOidcAccessToken(getOidcConfigName());

  const { data: userInfo, loading: loadingUserInfo } = useQuery(USER_INFO_QUERY);

  const { refetch } = useQuery(BRANDING_QUERY, {
    variables: { dealerId: dealer.id },
    onCompleted: data => {
      if (data) setLocalState({ ...data.brandingData });
    },
    skip: !dealer.id,
    fetchPolicy: 'network-only'
  });

  const { data: dealers } = useQuery(DEALERS_QUERY, {
    fetchPolicy: 'network-only'
  });

  const [saveApplicationTheme] = useMutation(SAVE_APPLICATION_THEME, {
    onError: () => {
      setIsSaving(prev => ({ ...prev, theme: false }));
    },
    skip: !dealer.id
  });

  useEffect(() => {
    if (userInfo?.userInfo.partnerId) setDealer({ id: userInfo.userInfo.partnerId, name: userInfo.userInfo.partnerName });
  }, [userInfo?.userInfo.partnerId, userInfo?.userInfo.partnerName]);

  const [uploadApplicationLogo] = useMutation(UPLOAD_APPLICATION_LOGO, {
    onError: () => {
      setIsSaving(prev => ({ ...prev, smallLogo: false }));
      setIsSaving(prev => ({ ...prev, logo: false }));
    },
    skip: !dealer.id
  });

  useSubscription(APPLICATION_THEME_SAVED, {
    onData: data => {
      addToast(t('Branding.ApplicationThemeSaved'), 'success');
      setIsDirty(prev => ({ ...prev, theme: false }));
      setIsSaving(prev => ({ ...prev, theme: false }));
      data.client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'applicationTheme' });
      refetch();
    }
  });

  useSubscription(APPLICATION_LOGO_UPLOADED, {
    onData: ({ data }) => {
      addToast(t('Branding.ApplicationLogoUpdated'), 'success');
      if (data.data.applicationLogoUploaded.isSmall) {
        setIsSaving(prev => ({ ...prev, smallLogo: false }));
        setIsDirty(prev => ({ ...prev, smallLogo: false }));
      } else {
        setIsSaving(prev => ({ ...prev, logo: false }));
        setIsDirty(prev => ({ ...prev, logo: false }));
      }
      refetch();
    }
  });

  const onLogoFileSelected = useCallback((event, isSmall = false) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (isSmall) {
        setSmallLogoFile(file);
        setIsDirty(prev => ({ ...prev, smallLogo: true }));
      } else {
        setLogoFile(file);
        setIsDirty(prev => ({ ...prev, logo: true }));
      }
    }
  }, []);

  const onSave = useCallback(() => {
    if (isDirty.logo) {
      uploadApplicationLogo({ variables: { dealerId: dealer.id, file: logoFile, isSmall: false } });
      setIsSaving(prev => ({ ...prev, logo: true }));
    }

    if (isDirty.smallLogo) {
      uploadApplicationLogo({ variables: { dealerId: dealer.id, file: smallLogoFile, isSmall: true } });
      setIsSaving(prev => ({ ...prev, smallLogo: true }));
    }

    if (isDirty.theme) {
      saveApplicationTheme({ variables: { dealerId: dealer.id, themeName: localState.themeName } });
      setIsSaving(prev => ({ ...prev, theme: true }));
    }
    let validationInput = {
      dealerId: dealer.id
    };

    if (!validate(validationInput)) return;
  }, [isDirty, uploadApplicationLogo, logoFile, smallLogoFile, saveApplicationTheme, localState.themeName, dealer.id, validate]);

  const onThemeChange = useCallback(value => {
    setLocalState(prev => ({ ...prev, themeName: value }));
    setIsDirty(prev => ({ ...prev, theme: true }));
  }, []);

  const onDealerChange = value => {
    const item = dealers.dealerBranding.find(x => x.partnerId === value);
    setDealer({ id: item.partnerId, name: item.partnerName });
  };
  const logoSource = useCallback(() => {
    return getDealerLogo(dealer.id, false, accessToken);
  }, [dealer.id, accessToken]);

  const smallLogoSource = useCallback(() => {
    return getDealerLogo(dealer.id, true, accessToken);
  }, [dealer.id, accessToken]);

  const isAnySaving = isSaving.logo || isSaving.smallLogo || isSaving.theme;
  const isAnyDirty = isDirty.logo || isDirty.smallLogo || isDirty.theme;
  return (
    userInfo &&
    !loadingUserInfo &&
    oidcUser && (
      <Card
        icon={Description}
        iconColor='primary'
        title={t('Branding.GeneralData')}
        actions={
          <IconButton
            color='transparent'
            type='save'
            fontSize='medium'
            tooltip={t('SaveChanges')}
            onClick={onSave}
            disabled={isAnySaving || !isAnyDirty}
          />
        }
      >
        <DealerBrandingComponent
          logoFileRef={logoFileRef}
          smallLogoFileRef={smallLogoFileRef}
          onLogoFileSelected={onLogoFileSelected}
          logoFileName={logoFile.name}
          smallLogoFileName={smallLogoFile.name}
          brandingData={localState}
          userInfo={userInfo.userInfo}
          onThemeChange={onThemeChange}
          logo={logoSource}
          smallLogo={smallLogoSource}
          dealers={dealers?.dealerBranding ?? emptyArray}
          onDealerChange={onDealerChange}
          validation={validation}
          dealer={dealer}
        />
      </Card>
    )
  );
};

export default DealerBrandingContainer;
